/**
 * Color Swatches - Unified functionality
 * 
 * Behavior:
 * - Desktop: Hover previews image, Click selects and locks the swatch
 * - Mobile: Tap selects and locks the swatch
 * - Quick-add button adds the currently selected/hovered product
 * - Clicking active swatch navigates to that product page
 */
(function() {
  'use strict';

  function initColorSwatches() {
    const swatchContainers = document.querySelectorAll('[data-color-swatches]');
    
    swatchContainers.forEach(container => {
      // Skip if already initialized
      if (container.dataset.initialized) return;
      container.dataset.initialized = 'true';
      
      // Find the parent product card
      const productCard = container.closest('product-card, .product-card');
      if (!productCard) return;
      
      // Find elements
      const mainImage = productCard.querySelector('[data-product-featured-image]');
      const secondaryImage = productCard.querySelector('.product-card__image--secondary');
      const mainLink = productCard.querySelector('.product-card__media');
      const quickAddForm = productCard.querySelector('product-form form');
      const quickBuyModal = productCard.querySelector('quick-buy-modal');
      
      if (!mainImage) return;
      
      // Store original values
      const originalData = {
        mainSrc: mainImage.src,
        mainSrcset: mainImage.srcset || '',
        secondarySrc: secondaryImage ? secondaryImage.src : null,
        secondarySrcset: secondaryImage ? (secondaryImage.srcset || '') : null,
        link: mainLink ? mainLink.getAttribute('href') : null,
        variantId: quickAddForm ? quickAddForm.querySelector('input[name="id"]')?.value : null,
        productHandle: quickBuyModal ? quickBuyModal.getAttribute('handle')?.split('?')[0] : null
      };
      
      // Track state
      let selectedSwatch = null;
      let hoveredSwatch = null;
      
      // Get all swatches
      const swatches = container.querySelectorAll('.color-swatch');
      
      // Function to update display
      function updateDisplay(swatch) {
        if (!swatch) {
          // Restore original
          mainImage.src = originalData.mainSrc;
          mainImage.srcset = originalData.mainSrcset;
          
          if (secondaryImage && originalData.secondarySrc) {
            secondaryImage.src = originalData.secondarySrc;
            secondaryImage.srcset = originalData.secondarySrcset;
          }
          
          if (mainLink && originalData.link) {
            mainLink.setAttribute('href', originalData.link);
          }
          
          // Restore single-variant form
          if (quickAddForm && originalData.variantId) {
            const idInput = quickAddForm.querySelector('input[name="id"]');
            if (idInput) idInput.value = originalData.variantId;
          }
          
          // Restore quick-buy modal handle
          if (quickBuyModal && originalData.productHandle) {
            quickBuyModal.setAttribute('handle', originalData.productHandle);
          }
          
          return;
        }
        
        const newImageUrl = swatch.dataset.swatchImage;
        const newProductUrl = swatch.getAttribute('href');
        const newVariantId = swatch.dataset.variantId;
        const newProductHandle = swatch.dataset.productHandle;
        
        // Update main image
        if (newImageUrl) {
          mainImage.src = newImageUrl;
          mainImage.srcset = '';
          
          if (secondaryImage) {
            secondaryImage.src = newImageUrl;
            secondaryImage.srcset = '';
          }
        }
        
        // Update link
        if (mainLink && newProductUrl) {
          mainLink.setAttribute('href', newProductUrl);
        }
        
        // Update single-variant quick-add form
        if (quickAddForm && newVariantId) {
          const idInput = quickAddForm.querySelector('input[name="id"]');
          if (idInput) idInput.value = newVariantId;
        }
        
        // Update quick-buy modal handle for multi-variant products
        if (quickBuyModal && newProductHandle) {
          // Include variant ID in handle for proper selection
          quickBuyModal.setAttribute('handle', `${newProductHandle}?variant=${newVariantId}`);
        }
      }
      
      // Function to update active classes
      function updateActiveClasses() {
        swatches.forEach(s => s.classList.remove('is-active', 'is-hovered'));
        if (selectedSwatch) selectedSwatch.classList.add('is-active');
        if (hoveredSwatch && hoveredSwatch !== selectedSwatch) {
          hoveredSwatch.classList.add('is-hovered');
        }
      }
      
      // Detect if touch device
      const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
      
      swatches.forEach(swatch => {
        // Click/Tap handler - selects and locks
        swatch.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          
          const productUrl = this.getAttribute('href');
          
          // If clicking already selected swatch, navigate to product
          if (this === selectedSwatch) {
            window.location.href = productUrl;
            return;
          }
          
          // Select this swatch
          selectedSwatch = this;
          hoveredSwatch = null;
          
          updateDisplay(this);
          updateActiveClasses();
        });
        
        // Desktop hover handlers
        if (!isTouchDevice) {
          swatch.addEventListener('mouseenter', function() {
            // Only preview if no swatch is selected
            if (!selectedSwatch) {
              hoveredSwatch = this;
              updateDisplay(this);
              updateActiveClasses();
            }
          });
          
          swatch.addEventListener('mouseleave', function() {
            if (!selectedSwatch && hoveredSwatch === this) {
              hoveredSwatch = null;
              updateDisplay(null);
              updateActiveClasses();
            }
          });
        }
      });
      
      // When mouse leaves the product card (desktop only)
      if (!isTouchDevice) {
        productCard.addEventListener('mouseleave', function() {
          selectedSwatch = null;
          hoveredSwatch = null;
          updateDisplay(null);
          updateActiveClasses();
        });
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initColorSwatches);
  } else {
    initColorSwatches();
  }

  document.addEventListener('shopify:section:load', initColorSwatches);
  document.addEventListener('ajaxinate:loaded', initColorSwatches);
  
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1 && (node.matches && (node.matches('product-card') || node.querySelector && node.querySelector('product-card')))) {
            initColorSwatches();
          }
        });
      }
    });
  });
  
  observer.observe(document.body, { childList: true, subtree: true });

})();


/**
 * Color Sidebar - Open/Close functionality
 */
(function() {
  'use strict';

  function initColorSidebar() {
    document.querySelectorAll('[data-open-color-sidebar]').forEach(button => {
      if (button.dataset.sidebarInitialized) return;
      button.dataset.sidebarInitialized = 'true';
      
      button.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        let sidebar = null;
        const wrapper = this.closest('.color-swatches-wrapper');
        
        if (wrapper) {
          sidebar = wrapper.querySelector('.color-sidebar');
        } else {
          const swatchContainer = this.closest('.color-swatches');
          sidebar = swatchContainer ? swatchContainer.nextElementSibling : null;
        }
        
        if (sidebar && sidebar.classList.contains('color-sidebar')) {
          sidebar.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        }
      });
    });

    document.querySelectorAll('[data-close-color-sidebar]').forEach(button => {
      if (button.dataset.closeInitialized) return;
      button.dataset.closeInitialized = 'true';
      
      button.addEventListener('click', function() {
        const sidebar = this.closest('.color-sidebar');
        if (sidebar) {
          sidebar.classList.remove('is-open');
          document.body.style.overflow = '';
        }
      });
    });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        document.querySelectorAll('.color-sidebar.is-open').forEach(sidebar => {
          sidebar.classList.remove('is-open');
          document.body.style.overflow = '';
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initColorSidebar);
  } else {
    initColorSidebar();
  }

  document.addEventListener('shopify:section:load', initColorSidebar);
})();